// Apertura di un URL esterno il cui valore arriva da una chiamata async
// (es. PDF firmato generato da una edge function: invoke → pdf_url → apri).
//
// PERCHÉ ESISTE — comportamento WebKit vs Chromium:
// WebKit (Safari desktop/mobile, la WebView iOS dell'app, E la PWA installata
// su iOS in modalità standalone) consente `window.open` su una nuova
// scheda SOLO se chiamato in modo sincrono dentro il gesto utente. Appena si
// attraversa un `await` (il round-trip che genera il PDF) l'attivazione utente
// è "consumata" e il pop-up viene BLOCCATO. Chromium (Chrome, WebView Android)
// mantiene l'attivazione viva attraverso il fetch async. Risultato classico:
// "su Chrome si apre, su Safari/iPhone no".
//
// Caso peggiore: PWA installata su iOS (standalone). Lì NON esistono
// schede/pop-up e `Capacitor.isNativePlatform()` è false. Un window.open
// differito non mostra nulla; anche pre-aprire una scheda vuota e redirigerla è
// inaffidabile. L'unico metodo solido è aprire l'URL REALE in un gesto fresco →
// per questo, su iOS-standalone, risolviamo l'URL e lasciamo che il chiamante
// mostri un bottone "Apri" (secondo tap = gesto fresco).
//
// Strategia per piattaforma:
//  - NATIVO (Capacitor): Capacitor Browser — API nativa, non soggetta al
//    popup-blocker, funziona anche dopo l'await. One-tap.
//  - WEB browser (Chrome, Safari tab, ecc.): riserviamo la scheda SINCRONA nel
//    gesto (prima dell'await) e la redirigiamo a URL pronto. One-tap.
//  - iOS PWA standalone: nessun pre-open; ritorniamo `blocked` con l'URL così
//    il chiamante mostra un bottone "Apri PDF" (secondo tap apre l'URL reale).

import { Capacitor } from '@capacitor/core';

export interface OpenExternalResult {
  ok: boolean;
  url?: string;
  /** true se l'apertura automatica non è possibile/affidabile (iOS standalone,
   *  o popup-blocker che ha rifiutato la scheda): il chiamante deve mostrare un
   *  link/bottone tappabile che riaprirà con un gesto fresco. */
  blocked?: boolean;
}

/** PWA installata su iOS in modalità standalone (home screen). `navigator.standalone`
 *  è una proprietà non-standard, vera SOLO in questo caso. */
function isIosStandalonePwa(): boolean {
  return typeof navigator !== 'undefined'
    && (navigator as unknown as { standalone?: boolean }).standalone === true;
}

/**
 * Risolve un URL via callback async e lo apre rispettando il popup-blocker di
 * WebKit. La scheda web viene riservata PRIMA dell'await: chiamare questa
 * funzione direttamente dall'handler di un gesto utente (onClick).
 */
export async function openExternalFromAsync(
  resolveUrl: () => Promise<string>,
): Promise<OpenExternalResult> {
  const isNative = Capacitor.isNativePlatform();
  const iosStandalone = !isNative && isIosStandalonePwa();

  // WEB browser non-standalone: riserva la scheda sincrona, dentro il gesto.
  // Su native o iOS-standalone NON pre-apriamo nulla.
  const win: Window | null = (isNative || iosStandalone) ? null : window.open('', '_blank');

  try {
    const url = await resolveUrl();
    if (!url) throw new Error('URL mancante');

    if (isNative) {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url, presentationStyle: 'popover' });
      return { ok: true, url };
    }

    // iOS standalone: non possiamo aprire una scheda dopo l'await → segnaliamo
    // blocked così il chiamante offre il bottone "Apri PDF" (gesto fresco).
    if (iosStandalone) {
      return { ok: false, url, blocked: true };
    }

    if (win && !win.closed) {
      win.opener = null; // hardening anti reverse-tabnabbing
      win.location.href = url;
      return { ok: true, url };
    }

    // Scheda bloccata in apertura (popup-blocker aggressivo): fallback bottone.
    return { ok: false, url, blocked: true };
  } catch (err) {
    if (win && !win.closed) win.close(); // chiudi la scheda bianca su errore
    throw err;
  }
}

/**
 * Apertura sincrona di un URL già noto (es. dal bottone "Apri PDF" dopo che la
 * generazione è finita). Essendo dentro un gesto fresco, su web `window.open`
 * passa anche in iOS standalone (apre l'URL reale in Safari).
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url, presentationStyle: 'popover' });
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
